import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';

import { FixtureBase, FixtureType } from '@cds/betting-offer';
import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import {
    AZSportsDataModel,
    LiveSectionConfig,
    PrettyUrlsConfig,
    SeoConfig,
    Sitecore,
    SportsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { CategoryType } from '@frontend/sports/navigation/core/feature/models';
import { SegmentNormalizer, UrlBuilder, UrlParam, UrlSegmentParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { RacingUrlParam } from '@frontend/sports/racing/feature/models';
import { DeviceService, Page, QuerySearchParams, UrlService } from '@frontend/vanilla/core';
import { toNumber } from 'lodash-es';

export enum LiveUrlParam {
    Highlights = 0,
    SportList = -1,
    Video = -2,
    Favourites = -3,
    Sport = 1,
    Esports = -4,
    LiveCasino = -5,
}

export enum SportUrlParam {
    Competitions = 'competitions',
    Calendar = 'calendar',
    Today = 'today',
    Betting = 'betting',
    Antepost = 'antepost',
    Featured = 'featured',
    TeamPages = 'teamPages',
    WorldCupHub = 'worldCupHub',
}

export enum SettingsUrlParam {
    Betslip = 'betslip',
    QuickBet = 'quickBet',
    EarlyPayout = 'earlyPayout',
    OddsDisplay = 'oddsDisplay',
    StakeChanges = 'stakeChanges',
}

export enum ModelPopupTypes {
    MyBets = 'my-bets',
    Betslip = 'betslip',
    BetModal = 'bet-modal',
}

export enum VirtualUrlParam {
    OnDemand = 'onDemand',
}

export enum CalendarUrlParam {
    In30minutes = 'in30minutes',
    In60minutes = 'in60minutes',
    In180minutes = 'in180minutes',
    Today = 'today',
    Tomorrow = 'tomorrow',
    After2days = 'after2days',
    After3days = 'after3days',
    Next2days = 'next2days',
    Next3days = 'next3days',
    Next5days = 'next5days',
    MidWeek = 'midWeek',
    ThisWeekend = 'thisWeekend',
}

export enum MyBetsUrlParam {
    Live = 'live',
    Open = 'open',
    Settled = 'settled',
}

export enum CouponUrlParam {
    BetBuilder = 'bet-builder',
}

export enum MarketUrlParam {
    MyBets = 'my-bets',
    BetBuilder = 'bet-builder',
    Sgp = 'SGP',
}

export interface EventLinkConfig {
    extraParams?: MarketUrlParam;
    emptyText: string;
}

export enum MarketOfferUrlParam {
    NonGridable = 'non-gridable',
    Gridable = 'matches',
    Outrights = 'outrights',
    Specials = 'specials',
    Standings = 'standings',
    Teams = 'teams',
    PriceBoost = 'priceboost',
}

export enum PriceBoostUrlParam {
    PriceBoostHub = 'priceBoost',
}

export enum FavouritesDashboardUrlParam {
    Upcoming = 'upcoming',
    Competitions = 'competitions',
    Players = 'players',
    Events = 'events',
}

export interface GameUrlParam {
    game: string;
    section: string;
    title?: string;
    provider?: string;
    lobbyPosition?: string;
    position?: number;
}

export interface LiveSportUrlOptions {
    sport: UrlParam;
    region?: UrlParam | LiveUrlParam.Sport;
    league?: UrlParam;
    conference?: UrlParam;
    isEsport?: boolean;
}

export interface BetsUrlOptions {
    sport: UrlParam;
    region?: UrlParam;
    league?: UrlParam;
    conference?: UrlParam;
    category?: string;
    subCategory?: Record<CategoryType, string | undefined>;
    dynamicCategory?: string;
}

@Injectable({ providedIn: 'root' })
export class UrlHelperService {
    protected page = inject(Page);
    private location = inject(Location);
    private urlService = inject(UrlService);
    protected urlConfig = inject(PrettyUrlsConfig);
    private sportConfig = inject(SportsConfig);
    protected sitecore = inject(Sitecore);
    private device = inject(DeviceService);
    private seoConfig = inject(SeoConfig);
    private azSportsData = inject(AZSportsDataModel);
    private liveConfig = inject(LiveSectionConfig);

    private sportsProductService = inject(SportsProductService);

    getUrl(url?: string): string {
        if (!url) {
            return '#';
        }

        if (url.startsWith('http')) {
            return url;
        }

        const firstChar = url.charAt(0);
        const locationPath = this.location.path();

        let updatedUrl = url;

        if (updatedUrl === '/') {
            updatedUrl = this.prependPrefix('');
        } else if (firstChar === '/') {
            updatedUrl = this.prependPrefix(url);
        } else if (firstChar === '?') {
            updatedUrl = `${locationPath}${url}`;
        } else {
            updatedUrl = `${locationPath}/${url}`;
        }

        return updatedUrl.replace(/\/$/, '');
    }

    getHomeUrl(): string {
        return this.prefix;
    }

    getMultiSportsHubUrl(): string {
        return `${this.prefix}/${this.urlConfig.translations.multiSports}/${this.urlConfig.translations.multiSportsHubName}`;
    }

    getESportsUrl(): string {
        const productPath = this.sportsProductService.isSingleDomain ? `${this.sportsProductService.sportsProductName}/` : '';
        return `/${this.page.lang}/${productPath}${this.urlConfig.translations.esports}`;
    }

    getSearchUrl(): string {
        return `${this.prefix}?popup=betfinder`;
    }

    getFindMenuUrl(): string {
        return `${this.prefix}/find`;
    }

    getFindMenuSearchUrl(): string {
        return `${this.prefix}/find/search`;
    }

    getMyBetPopupUrl(): string {
        return `${this.prefix}?popup=my-bets`;
    }

    getBetModalUrl(): string {
        return `${this.prefix}?popup=bet-modal`;
    }

    getAtoZUrl(): string {
        return `/${this.page.lang}/product-menu`;
    }

    getCalendarUrl(interval?: string | CalendarUrlParam): string {
        if (!interval) {
            return this.getBuilder(this.urlConfig.translations.calendar).toString();
        }

        const translations: Record<string, string | undefined> = { ...this.urlConfig.translations };

        const allowed: string[] = Object.values(CalendarUrlParam);
        const allowedTranslations = allowed.map((key) => translations[key]);

        if (interval in CalendarUrlParam && translations[interval]) {
            return this.getBuilder(translations[interval]).toString();
        }

        if (allowed.includes(interval) && translations[interval]) {
            return this.getBuilder(translations[interval]).toString();
        }

        if (allowedTranslations.includes(interval)) {
            return this.getBuilder(interval).toString();
        } else {
            throw new Error('Wrong calendar interval');
        }
    }

    getLiveUrl(tab: LiveUrlParam = LiveUrlParam.Highlights, tracking?: string): string {
        const map = {
            [LiveUrlParam.Video]: this.urlConfig.translations.video,
            [LiveUrlParam.SportList]: this.urlConfig.translations.all,
            [LiveUrlParam.Highlights]: this.urlConfig.translations.betting,
            [LiveUrlParam.Favourites]: this.urlConfig.translations.favourites,
            [LiveUrlParam.Esports]: this.urlConfig.translations.esports,
            // TODO: this is not available in the config
            // [LiveUrlParam.Sport]: this.urlConfig.translations.sport,
            [LiveUrlParam.LiveCasino]: this.urlConfig.translations.liveCasino,
        };

        const mapped = map[tab as keyof typeof map];
        const builder = this.getBuilder(this.urlConfig.translations.live, mapped);

        if (tracking) {
            builder.appendQuery({ trid: tracking });
        }

        return builder.toString();
    }

    getPriceBoostUrl(): string {
        // TODO: this is not available in the config
        // return this.getBuilder(this.urlConfig.translations.priceBoost).toString();
        return this.getBuilder().toString();
    }

    getLiveSportUrl({ sport, region, league, isEsport, conference }: LiveSportUrlOptions): string {
        if (isEsport && !this.liveConfig.allowedEsportsOnLiveNavigation?.includes(Number(sport.id))) {
            return this.getLiveUrl(LiveUrlParam.Esports);
        }

        const builder = this.getBuilder(this.urlConfig.translations.live, sport);

        if (region) {
            if (region === LiveUrlParam.Sport) {
                builder.appendQuery({ fallback: false });
            } else {
                builder.append(region);
            }
        }

        if (league) {
            builder.append(league);
        }

        if (conference) {
            builder.append(conference);
        }

        return builder.toString();
    }

    getVirtualSportUrl(param?: VirtualUrlParam.OnDemand): string;
    getVirtualSportUrl(sport: UrlParam, competition?: UrlParam, event?: string): string;
    getVirtualSportUrl(sportOrParam?: UrlParam | VirtualUrlParam.OnDemand, competition?: UrlParam, event?: string): string {
        const builder = this.getBuilder(this.urlConfig.translations.virtual);

        if (sportOrParam) {
            if (sportOrParam === VirtualUrlParam.OnDemand) {
                builder.append(this.urlConfig.translations.onDemand);
            } else {
                builder.append(sportOrParam);
            }
        }

        if (competition) {
            builder.append(competition);

            if (event) {
                builder.appendQuery({ tab: event });
            }
        }

        return builder.toString();
    }

    getTournamentUrl(sport: UrlParam, tournament: string, league?: UrlParam): string {
        const builder = this.getBuilder(sport, this.urlConfig.translations.tournaments, tournament);

        if (league) {
            builder.append(league);
        }

        return builder.toString();
    }

    getEventUrl(event?: UrlParam, market?: MarketUrlParam): string {
        const builder = this.getBuilder(this.urlConfig.translations.events);

        if (!event) {
            return builder.toString();
        }

        return builder.append(event).appendQuery({ market }).toString();
    }

    getFixtureUrl(sport: UrlParam, fixture: UrlParam, prefix?: string): string {
        return prefix
            ? this.getBuilder(prefix, sport, this.urlConfig.translations.fixtures, fixture).toString()
            : this.getBuilder(sport, this.urlConfig.translations.fixtures, fixture).toString();
    }

    getFixtureUrlByFixture(fixtureLike: Pick<FixtureBase, 'id' | 'name' | 'sport' | 'fixtureType'>, prefix?: string): string {
        const fixture: UrlParam = {
            id: fixtureLike.id,
            name: translationToStr(fixtureLike.name),
        };
        const sport: UrlParam = {
            id: fixtureLike.sport.id,
            name: translationToStr(fixtureLike.sport.name),
        };
        switch (fixtureLike.fixtureType) {
            case FixtureType.Antepost:
                return prefix
                    ? this.getBuilder(prefix, sport, this.urlConfig.translations.fixtures, fixture).toString()
                    : this.getBuilder(sport, this.urlConfig.translations.fixtures, fixture).toString();
            case FixtureType.DayOfRace:
                const dayOfRaceFixture = fixtureLike as Pick<FixtureBase, 'id' | 'meeting'>;

                return this.getRacingUrlByFixture(fixtureLike.sport.id, dayOfRaceFixture);
            default:
                return '';
        }
    }

    getCouponUrl(sport: UrlParam, coupon?: UrlParam | CouponUrlParam): string {
        const builder = this.getBuilder(sport, this.urlConfig.translations.coupons);

        if (coupon) {
            if (coupon === CouponUrlParam.BetBuilder) {
                builder.append(this.urlConfig.translations.betBuilder);
            } else {
                builder.append(coupon);
            }
        }

        return builder.toString();
    }

    //TODO consider refactoring the service into smaller peaces

    getCompetitionCouponUrl(sport: UrlParam, coupon?: UrlParam | CouponUrlParam, region?: UrlParam, league?: UrlParam): string {
        const builder = this.getBuilder(sport, this.urlConfig.translations.coupons);
        if (region) {
            builder.append(region);
        }

        if (league) {
            builder.append(league);
        }

        if (coupon) {
            if (coupon === CouponUrlParam.BetBuilder) {
                builder.append(this.urlConfig.translations.betBuilder);
            } else {
                builder.append(coupon);
            }
        }

        return builder.toString();
    }

    getMultiBuilderUrl(league?: UrlParam): string {
        const builder = this.getBuilder(this.urlConfig.translations.multiBuilder);

        if (league) {
            builder.append(league);
        }

        return builder.toString();
    }

    getBetsUrl({ sport, region, league, conference, category, subCategory, dynamicCategory }: BetsUrlOptions): string {
        const builder = this.getBuilder(this.urlConfig.translations.bets).append(sport).appendIf(region).appendIf(league).appendIf(conference);

        if (category) {
            builder.appendQuery({ category });
        }
        if (subCategory?.outrightCategory || subCategory?.specialCategory) {
            builder.appendQuery(subCategory);
        }

        if (dynamicCategory) {
            builder.appendQuery({ dynamicCategory });
        }

        return builder.toString();
    }

    getBettingUrl({ sport, region, league, conference }: BetsUrlOptions): string {
        const builder = this.getBuilder(sport);

        if (region) {
            builder.append(this.urlConfig.translations.betting).append(region).appendIf(league).appendIf(conference);
        }

        return builder.toString();
    }

    getStandingsUrl(sport: UrlParam, region?: UrlParam, league?: UrlParam, group?: UrlParam): string {
        const builder = this.getBuilder(this.urlConfig.translations.standings);
        builder.append(sport);
        if (region) {
            builder.append(region);
        }
        if (league) {
            builder.append(league);
        }
        if (group) {
            builder.append(group);
        }

        return builder.toString();
    }

    getTeamPagesUrl(sport: UrlParam, team?: UrlParam, league?: UrlParam): string {
        const builder = this.getBuilder(sport, this.urlConfig.translations.teamPages);
        builder.append(league || this.urlConfig.translations.myTeams);
        if (team) {
            builder.append(team);
        }

        return builder.toString();
    }

    getTeamPagesSearchUrl(sportUrl: string, sportId: string | number | undefined): string {
        return `${sportUrl}&teamId=${sportId}`;
    }

    getSettingsUrl(settings: SettingsUrlParam): string {
        return this.getBuilder(this.urlConfig.translations.settings, this.urlConfig.translations[settings]).toString();
    }

    getMyBetsUrl(page?: MyBetsUrlParam, params?: Params): string {
        const builder = this.getMyBetsUrlBuilder(page);
        if (params) {
            builder.appendQuery(params);
        }

        return builder.toString();
    }

    getPrintUrlBuilder(): UrlBuilder {
        const builder = this.getMyBetsUrlBuilder();
        builder.append('print');

        return builder;
    }

    shouldRemoveHref(): boolean {
        return this.seoConfig.removeHrefToEventForRobot && this.device.isRobot;
    }

    private getMyBetsUrlBuilder(page?: MyBetsUrlParam): UrlBuilder {
        const builder = this.getBuilder(this.urlConfig.translations.myBets);

        if (page) {
            builder.append(this.urlConfig.translations[page]);
        }

        return builder;
    }

    /**
     * Used to generate sport pages specific urls
     * - sport list url (with fallback from SportsConfig.competitionLandingPage)
     * - sport lobby url (with fallback from SportsConfig.competitionLandingPage)
     * - sport competition page with specific context (all fixtures for that context)
     * - sport comeptition list for specific context (all, today, ....)
     * - sport specific pages (calendar, force lobby)
     *
     * @param {UrlParam} sport sport for which we want to generate the url, if undefined returns all sports url
     * @param {string | SportUrlParam | CalendarUrlParam} context context for which we want to generate the url for, can be a translated
     * token (from PrettyUrlsConfig.translations) or a SportUrl param, and the value will be tested agains the allowed walues, otherwise
     * serice throws. SportUrlParam.Featured which is being used only to force sports with compeittion landing page to
     * generate sport featured url and the context is not used in the url
     * @param {SportUrlParam.Competitions} subContext the sub-context for the url generation, only allowed way is SportUrlParam.Competitions
     * and when used will generate urls for competitions list for specific context
     * @returns {string} url for the requested params
     * @throws when specified context is not a valid token
     * @example
     * // all the examples suppose that the language is de and golf is configured to have competition landing page
     *
     * // returns /de/sports/alle
     * urlHelper.getSportUrl();
     *
     * // returns /de/sports/fußball-4
     * urlHelper.getSportUrl(football);
     * urlHelper.getSportUrl(football, SportUrlParam.Featured);
     *
     * // returns /de/sports/golf-13?fallback=true
     * urlHelper.getSportUrl(golf);
     *
     * // returns /de/sports/golf-13
     * urlHelper.getSportUrl(golf, SportUrlParam.Featured);
     *
     * // returns /de/sports/fußball-4/nach-2-tagen
     * urlHelper.getSportUrl(football, CalendarUrlParam.After3day);
     * urlHelper.getSportUrl(football, 'nach-2-tagen');
     *
     * // returns /de/sports/fußball-4/wetten/wettbewerben
     * urlHelper.getSportUrl(football, SportUrlParam.Betting, SportUrlParam.Competitions);
     *
     * // returns /de/sports/fußball-4/kalendar
     * urlHelper.getSportUrl(football, SportUrlParam.Calendar);
     */
    getSportUrl(sport?: UrlParam, context?: string | SportUrlParam | CalendarUrlParam, subContext?: SportUrlParam.Competitions): string;
    /**
     * Used to generate sport fixture list pages with different contexts and levels
     *
     * @param {UrlParam} sport sport for which we want to generate the url
     * @param {string | SportUrlParam | CalendarUrlParam} context context for which we want to generate the url for, can be a translated
     * token (from PrettyUrlsConfig.translations) or a SportUrl param, and the value will be tested agains the allowed walues, otherwise
     * serice throws.
     * @param {UrlParam} region the region for which we want the fixture list to be filtered
     * @param {UrlParam} league the league (or virtual competition) for which we want the fixture list to be filtered
     * @param {UrlParam} group the group (virtual competition group) or conference for which we want the fixture list to be filtered
     * @param {tab} tab the tab of the specific market offer (matches/outrights/specials) for which we want the fixture list to be filtered
     * @param {shouldApplyFallback} shouldApplyFallback should {sportId}?fallback=true to be applied for special sports
     * @returns {string} url for the requested params
     * @throws when specified context is not a valid token
     * @example
     * // returns /de/sports/fußball-4/nach-2-tagen
     * urlHelper.getSportUrl(football, CalendarUrlParam.After3day);
     * urlHelper.getSportUrl(football, 'nach-2-tagen');
     *
     * // returns /de/sports/fußball-4/wetten/welt-6
     * urlHelper.getSportUrl(football, SportUrlParam.Betting, world);
     *
     * // returns /de/sports/fußball-4/wetten/welt-6/bundesliga-43
     * urlHelper.getSportUrl(football, SportUrlParam.Betting, world, bundesliga);
     *
     * // returns /de/sports/fußball-4/wetten/welt-6/bundesliga-43
     * urlHelper.getSportUrl(football, SportUrlParam.Betting, world, bundesliga);
     *
     * // returns /de/sports/fußball-4/wetten/welt-6/weltmeisterschaft-100
     * urlHelper.getSportUrl(football, SportUrlParam.Betting, world, worldCup);
     *
     * // returns /de/sports/fußball-4/wetten/welt-6/weltmeisterschaft-100/gruppe-a-1
     * urlHelper.getSportUrl(football, SportUrlParam.Betting, world, worldCup, groupA);
     *
     * // returns /de/sports/fußball-4/wetten/welt-6/weltmeisterschaft-100/gruppe-a-1?tab=outrights
     * urlHelper.getSportUrl(football, SportUrlParam.Betting, world, worldCup, groupA, 'outrights');
     */
    getSportUrl(
        sport?: UrlParam,
        context?: string | SportUrlParam | CalendarUrlParam,
        region?: UrlParam,
        league?: UrlParam,
        group?: UrlParam,
        tab?: string,
        shouldApplyFallback?: boolean,
    ): string;

    //TODO consider a refactor if you are modifying this function.
    // eslint-disable-next-line max-lines-per-function
    getSportUrl(
        sport?: UrlParam,
        context?: string,
        regionOrSubContext?: UrlParam | string,
        league?: UrlParam,
        group?: UrlParam,
        tab?: string,
        shouldApplyFallback = true,
    ): string {
        if (!sport) {
            return this.getBuilder(this.urlConfig.translations.all).toString();
        }

        const builder = this.getSportBuilder(sport);
        // we need to bypass the additional checks so we use the Featured context on the caller
        if (context === SportUrlParam.Featured || (!context && this.device.isRobot)) {
            return builder.toString();
        }

        if (!context) {
            const fallback = shouldApplyFallback && this.sportConfig.competitionLandingPage.includes(toNumber(sport.id));
            if (!this.azSportsData.azSports.some((item) => item.activeName === sport.name)) {
                return builder.appendQuery({ fallback: fallback || undefined }).toString();
            } else if (this.azSportsData.azSports.length > 0) {
                //it's already checked on line 607, hence using null assertion
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return this.azSportsData.azSports.find((item) => item.activeName === sport.name)!.link;
            }
        }

        const appendContext = () => {
            if (context) {
                const match = Object.entries(this.urlConfig.translations).find(([key, value]) => value === context || key === context);

                if (match) {
                    builder.append(match[1]);
                } else {
                    throw new Error('Context is not valid value');
                }
            }

            return builder;
        };

        if (regionOrSubContext === SportUrlParam.Competitions) {
            appendContext().append(this.urlConfig.translations.competitions);
        } else {
            if (sport.id === SportConstant.Horses || sport.id === SportConstant.Greyhounds) {
                throw new Error(`Context is not valid for ${sport.id === SportConstant.Horses ? 'horse' : 'greyhounds'} racing`);
            } else if (context === SportUrlParam.Antepost) {
                return builder.append(this.urlConfig.translations.eachWay).toString();
            } else {
                appendContext();
            }

            if (regionOrSubContext) {
                builder.append(regionOrSubContext);

                if (league) {
                    builder.append(league);

                    if (group) {
                        builder.append(group);
                    }
                }
            }
        }

        if (tab) {
            builder.appendQuery({ tab });
        }

        return builder.toString();
    }

    getPopupUrl(popupName: string): string {
        const url = this.urlService.current();
        url.search.set('popup', popupName);

        return url.url();
    }

    getFavouriteUrl(name?: string): string {
        const builder = this.getBuilder(this.urlConfig.translations.favourites);

        if (name) {
            builder.append(name);
        }

        return builder.toString();
    }

    getFavouriteUrlByUrlParam(favouritesUrlParam: FavouritesDashboardUrlParam): string {
        const builder = this.getBuilder(this.urlConfig.translations.favourites);
        const name = this.urlConfig.translations[favouritesUrlParam];
        if (name) {
            builder.append(name);
        }

        return builder.toString();
    }

    getRaceUrl(sportId: SportConstant, param: RacingUrlParam | UrlParam, filterOrRace?: RacingUrlParam | UrlParam): string {
        const sport = {
            id: sportId,
            name:
                sportId === SportConstant.Horses ? this.sitecore.horseRacing.common.HorsesLinkText : this.sitecore.horseRacing.common.GreyhoundRacing,
        };

        const builder = this.getSportBuilder(sport);

        if (
            param === RacingUrlParam.Future ||
            param === RacingUrlParam.Today ||
            param === RacingUrlParam.Tomorrow ||
            param === RacingUrlParam.NextRaces
        ) {
            return builder.append(this.urlConfig.translations[param]).toString();
        }

        return this.getRacingUrl(param, builder, filterOrRace);
    }

    protected getRacingUrl(param: RacingUrlParam | UrlParam, builder: UrlBuilder, filterOrRace?: RacingUrlParam | UrlParam): string {
        if (typeof param === 'object') {
            builder.append(param);

            const filterParam = filterOrRace || RacingUrlParam.Today;

            for (const filter of [RacingUrlParam.Today, RacingUrlParam.Tomorrow]) {
                const entry = Object.entries(this.urlConfig.translations).find(([key, _]) => key === filter);
                if (filterParam === filter || filterParam === entry?.[1]) {
                    builder.append(entry?.[1]);
                }
            }
            if (typeof filterOrRace === 'object') {
                builder.append(filterOrRace);
            }

            return builder.toString();
        } else {
            throw new Error('Param is not valid for horse racing');
        }
    }

    getRacingUrlByFixture(
        sportId: SportConstant,
        fixtureLike: Pick<FixtureBase, 'id' | 'meeting'>,
        dayOfRace?: DayOfRaceDate,
        isAllRaceUrl = false,
    ): string {
        const meetingParam = {
            id: fixtureLike.meeting.id,
            name: translationToStr(fixtureLike.meeting.name),
        };

        if (isAllRaceUrl) {
            //below rule is mixing array concat and string concat and the auto fix causes bugs
            // eslint-disable-next-line unicorn/prefer-spread
            const dayRoute = (dayOfRace === DayOfRaceDate.Today ? RacingUrlParam.Today : RacingUrlParam.Tomorrow).concat('/all');
            const allRaceParam: UrlParam = {
                id: dayRoute,
                name: dayRoute,
            };

            return this.getRaceUrl(sportId, meetingParam, allRaceParam);
        }

        return this.getRaceUrl(sportId, meetingParam, { id: fixtureLike.id, name: '' });
    }

    getBuilder(...params: UrlSegmentParam[]): UrlBuilder {
        return this.getBuilderInternal(params);
    }

    protected getBuilderInternal(params: UrlSegmentParam[], caseSensitive = false): UrlBuilder {
        const normalizer = new SegmentNormalizer(this.urlConfig, caseSensitive);

        const builder = new UrlBuilder(normalizer);

        builder.append(this.page.lang).append(this.productPath);
        params.forEach((param) => builder.append(param));

        return builder;
    }

    protected getSportBuilder(...params: UrlSegmentParam[]): UrlBuilder {
        return this.getBuilder(...params);
    }

    getAbsolute(relativeUrl = ''): string {
        const url = this.urlService.current();
        let absUrl = `${url.protocol}://${url.hostname}`;
        if (!relativeUrl) {
            absUrl += this.prefix;
        }
        absUrl += relativeUrl;

        return absUrl;
    }

    getLastSegment(url: string): string {
        const parsed = this.urlService.parse(url);

        return parsed.pathname.split('/').pop() || parsed.pathname;
    }

    getQueryParams(url?: string): QuerySearchParams | undefined {
        if (url) {
            return this.urlService.parse(url).search;
        }

        return;
    }

    private prependPrefix(url: string): string {
        const isEsports = url.includes(this.urlConfig.translations.esports);

        if (!isEsports && !url.includes(this.prefix) && !url.startsWith(this.culturePrefix)) {
            return this.prefix + url;
        }

        return url;
    }

    private get prefix(): string {
        return `${this.culturePrefix}${this.productPath}`;
    }

    private get culturePrefix(): string {
        return `/${this.page.lang}/`;
    }

    get productPath(): string {
        return this.sportsProductService.isSingleDomain ? this.sportsProductService.sportsProductName : this.page.product;
    }
}
